// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomQuoteModel } from "@ces/App/Services/QuoteService";
import { CustomQuotesState } from "@ces/App/Store/Selectors/xQuotesSelector";
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { GetQuotesApiParameter } from "@insite/client-framework/Services/QuoteService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { setDataViewLoaded, setDataViewLoading } from "@insite/client-framework/Store/Data/DataState";
import { QuotesDataView, QuotesState } from "@insite/client-framework/Store/Data/Quotes/QuotesState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { BaseModel, PaginationModel, QuoteCollectionModel, QuoteModel } from "@insite/client-framework/Types/ApiModels";
import { Draft } from "immer";

export interface CustomQuoteCollectionModel extends BaseModel {
    pagination: PaginationModel | null;
    quotes: CustomQuoteModel[] | null;
}

const initialState: CustomQuotesState = {
    isLoading: {},
    byId: {},
    dataViews: {},
    errorStatusCodeById: {},
};

const reducer = {
    "Data/CustomQuotes/BeginLoadQuotes": (draft: Draft<CustomQuotesState>, action: { parameter: GetQuotesApiParameter }) => {
        setDataViewLoading(draft, action.parameter);
    },

    "Data/CustomQuotes/CompleteLoadQuotes": (
        draft: Draft<CustomQuotesState>,
        action: { parameter: GetQuotesApiParameter; collection: CustomQuoteCollectionModel },
    ) => {
        setDataViewLoaded(
            draft,
            action.parameter,
            action.collection,
            collection => collection.quotes!,
            undefined,
        );
    },
    "Data/CustomQuotes/BeginLoadQuote": (draft: Draft<CustomQuotesState>, action: { quoteNumber: string }) => {
        draft.isLoading[action.quoteNumber] = true;
    },
    "Data/CustomQuotes/CompleteLoadQuote": (draft: Draft<CustomQuotesState>, action: { quote: QuoteModel }) => {
        delete draft.isLoading[action.quote.quoteNumber];
        draft.byId[action.quote.quoteNumber] = action.quote;
        if (draft.errorStatusCodeById) {
            delete draft.errorStatusCodeById[action.quote.quoteNumber];
        }
    },
    "Data/CustomQuotes/FailedToLoadQuote": (draft: Draft<CustomQuotesState>, action: { quoteNumber: string; status: number }) => {
        delete draft.isLoading[action.quoteNumber];
        if (draft.errorStatusCodeById) {
            draft.errorStatusCodeById[action.quoteNumber] = action.status;
        }
    },
    "Data/CustomQuotes/Reset": () => {
        return initialState;
    },
};

const xCustomQuotes = createTypedReducerWithImmer(initialState, reducer);

(reducers as any).xCustomQuotes = xCustomQuotes;

export type xCustomQuotesState = {
    xCustomQuotes: CustomQuotesState;
} & ApplicationState;

type xCustomQuotesReducers = {
    [key: string]: typeof xCustomQuotes;
};

export type xCustomQuotesActions = Parameters<xCustomQuotesReducers[keyof xCustomQuotesReducers]>[1] | AnyAction;
